import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, FlaskConical, Languages } from "lucide-react";
import { PageHero } from "../../About";
import FacultyCard, { HodFacultyCard } from "../shared/FacultyCard";
import basicScienceFacultyData from "./facultyData/basicScienceFacultyData";

const facultyOrder = [
  "f-sneha-s-kulkarni",
  "f-g-s-kurdekar",
  "f-sameer-m-galagali",
  "f-m-s-sannaikar",
  "f-mallikarjun-k-patil",
  "f-meenal-m-kaliwal",
  "f-satish-hande",
  "f-vijaya-chitrali",
  "f-akshata-b-patil",
];

const getOrderedFacultyMembers = (members = []) => [
  ...facultyOrder
    .map((id) => members.find((faculty) => faculty.id === id))
    .filter(Boolean),
  ...members.filter((faculty) => !facultyOrder.includes(faculty.id)),
];

const facultyData = {
  ...basicScienceFacultyData,
  members: getOrderedFacultyMembers(basicScienceFacultyData.members),
};

const hodFacultyData = {
  ...facultyData.hod,
  name: "Dr. Vinod Naik",
};

export const programme = {
  id: "bsh",
  name: "Basic Science & Humanities",
  short: "BSH",
  programmeType: "ug",
  programmeLabel: "Basic Science",
  duration: "First-year engineering foundation",
  intake: null,
  established: 2004,
  description:
    "Basic Science & Humanities supports first-year engineering education through physics, chemistry, mathematics, English, and humanities foundations.",
  image: "/HomePage/dept/BSH.jpeg",
  hodId: "f-vinod-naik",
  labs: ["Physics Lab", "Chemistry Lab", "Language Lab", "Mathematics Learning Centre"],
  research: "Physics · Chemistry · Mathematics · English",
  accreditation: ["AICTE Approved", "NAAC Accredited (Institution)", "VTU-affiliated first-year curriculum"],
  achievements: [
    {
      title: "Achievement Documents",
      items: [
        { label: "BSH Achievements", href: "#" },
      ],
    },
  ],
  projects: [],
  nonTeachingStaff: [
    ["Mr. Rajesh Nirwani", "Foreman"],
    ["Smt. Swati Lokare", "Instructor"],
    ["Mr. Nadeem Naragund", "Attender"],
  ],
};

const departmentDashboard = {
  title: "Departments Dashboard",
  columns: ["Dashboard Metric", "Details"],
  rows: [
    ["Department Established", "2004"],
    ["Affiliated University", "Visvesvaraya Technological University (VTU), Belagavi"],
    ["Accreditation", "AICTE Approved | NAAC Accredited (Institution)"],
    ["Total Faculty", "10"],
    ["Faculty with Ph.D.", "08"],
    ["Faculty with M.Sc./M.Tech.", "02"],
    ["Physics Laboratory", "1 (Fully Equipped)"],
    ["Chemistry Laboratory", "1 (Fully Equipped)"],
    ["Students Served (per year)", "Approx. 450 (All 1st Year UG Engineering students)"],
    ["Courses Offered", "Engineering Physics, Engineering Chemistry, Applied Mathematics I & II, English I & II"],
  ],
};

const subjectProgrammes = [
  {
    id: "physics",
    to: "/programme/bsh/physics",
    short: "Physics",
    name: "Department of Physics",
    icon: FlaskConical,
    image: "/Academics/physics.jpg",
    description:
      "Builds conceptual and experimental foundations in engineering physics, optics, quantum mechanics, materials science, spectroscopy, and applied laboratory practice.",
    highlights: ["Engineering Physics", "Physics Laboratory", "Modern physics", "Material science"],
  },
  {
    id: "chemistry",
    to: "/programme/bsh/chemistry",
    short: "Chemistry",
    name: "Department of Chemistry",
    icon: FlaskConical,
    image: "/Academics/chemisty.jpg",
    description:
      "Introduces engineering chemistry through electrochemistry, corrosion control, water analysis, polymers, fuels, materials, and chemistry laboratory work.",
    highlights: ["Engineering Chemistry", "Chemistry Laboratory", "Electrochemistry", "Polymers & fuels"],
  },
  {
    id: "mathematics",
    to: "/programme/bsh/maths",
    short: "Maths",
    name: "Department of Mathematics",
    icon: BookOpen,
    image: "/Campus/maths.jpg",
    description:
      "Strengthens analytical thinking through applied mathematics, calculus, linear algebra, transforms, numerical methods, and problem-solving for engineering.",
    highlights: ["Applied Mathematics I", "Applied Mathematics II", "Linear algebra", "Numerical methods"],
  },
  {
    id: "english",
    to: "/programme/bsh/english",
    short: "English",
    name: "Department of English",
    icon: Languages,
    image: "/Campus/english.jpg",
    description:
      "Develops communication skills, professional English, presentation ability, technical writing, teamwork, and confidence for first-year engineering students.",
    highlights: ["English I", "English II", "Communication skills", "Presentation practice"],
  },
];

const BasicScience = () => {
  return (
    <main>
      <PageHero
        title="Basic Science & Humanities"
        sub="First-year engineering foundation through Physics, Chemistry, Maths, and English"
        image="/Academics/hero.jpg"
      />

      <section className="bg-surface-alt/60 border-b border-brand/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap gap-x-6 gap-y-2 text-xs font-sans-ui font-semibold tracking-wider text-brand">
          <a href="#departments" className="uppercase hover:underline">
            Programmes
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <h2
          className="text-3xl md:text-4xl text-brand mb-8 font-semibold"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {departmentDashboard.title}
        </h2>
        <div className="bg-white border border-brand/15 overflow-hidden">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-brand text-surface">
              <tr>
                {departmentDashboard.columns.map((column) => (
                  <th
                    key={column}
                    className="text-left px-4 py-3 font-semibold align-top"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {departmentDashboard.rows.map((row) => (
                <tr key={row.join("-")} className="odd:bg-surface/40">
                  {row.map((cell, index) => (
                    <td
                      key={`${cell}-${index}`}
                      className="px-4 py-3 border-t border-brand/10 align-top whitespace-normal break-words"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section id="faculty" className="max-w-7xl mx-auto px-6 lg:px-10 pb-14">
        <div className="mb-10 aspect-video w-full overflow-hidden bg-black">
          <iframe
            className="h-full w-full"
            src="https://www.youtube.com/embed/rdhbTmdLd8I"
            title="Basic Science and Humanities department video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <h2
          className="text-3xl md:text-4xl text-brand mb-8 font-semibold"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Head of the Department
        </h2>
        <HodFacultyCard faculty={hodFacultyData} />
        {facultyData.members?.length > 0 && (
          <>
            <h3
              className="text-2xl md:text-3xl text-brand mt-10 mb-5 font-semibold"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Faculty Members
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {facultyData.members.map((faculty) => (
                <FacultyCard key={faculty.id} faculty={faculty} />
              ))}
            </div>
          </>
        )}
      </section>

      <section id="departments" className="bg-surface-alt/60 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2
            className="text-3xl md:text-4xl text-brand mb-10 font-semibold"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Programmes
          </h2>
          <div className="space-y-12">
            {subjectProgrammes.map((programmeItem, idx) => (
              <div
                key={programmeItem.id}
                id={programmeItem.id}
                className="grid md:grid-cols-2 gap-8 items-center"
              >
                <Link
                  to={programmeItem.to}
                  className={`aspect-[4/3] overflow-hidden block ${idx % 2 === 1 ? "md:order-2" : ""}`}
                >
                  <img
                    src={programmeItem.image}
                    alt={programmeItem.name}
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </Link>
                <div className={`${idx % 2 === 1 ? "md:order-1" : ""}`}>
                  <Link
                    to={programmeItem.to}
                    className="block hover:underline"
                  >
                    <h3
                      className="text-2xl md:text-3xl text-brand font-semibold mt-2 leading-tight"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {programmeItem.name}
                    </h3>
                  </Link>
                  <p className="mt-3 text-[#2a2a2a]/85 leading-relaxed">
                    {programmeItem.description}
                  </p>
                
                  <Link
                    to={programmeItem.to}
                    className="inline-flex items-center gap-1 mt-5 text-brand font-sans-ui text-sm font-semibold hover:underline"
                  >
                    View programme
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

const Card = ({ icon: Icon, title, body }) => (
  <div className="bg-white p-8 border border-brand/10 card-hover">
    <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center text-brand mb-4">
      <Icon size={22} />
    </div>
    <h3
      className="text-2xl text-brand font-semibold mb-2"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      {title}
    </h3>
    <p className="text-[#3a3a3a]/85 text-sm leading-relaxed">{body}</p>
  </div>
);

export default BasicScience;
